/**
 * Client-side structured logger for packages/shared (browser environment).
 *
 * Mirrors the pino log-level API (debug / info / warn / error).
 *
 * In development: all levels print to the browser console.
 * In production:  debug/info are suppressed; warn/error are captured by Sentry
 *                 if window.Sentry has been initialised by the app, and are
 *                 otherwise silently dropped (no sensitive context reaches DevTools).
 */

// Safe guard for non-Vite bundlers (Jest, CJS tsup output) where import.meta may be undefined.
const isDev: boolean =
  typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV === true

type LogContext = Record<string, unknown>

function getSentry(): any {
  return typeof window !== 'undefined' ? (window as any).Sentry : undefined
}

function sentryCapture(level: 'warning' | 'error', context: LogContext, message: string): void {
  const S = getSentry()
  if (!S) return
  if (level === 'error' && context.err instanceof Error) {
    S.captureException(context.err, { extra: context })
  } else {
    S.captureMessage(message, level, { extra: context })
  }
}

export const clientLogger = {
  debug(context: LogContext, message: string): void {
    if (isDev) console.debug(message, context)
  },

  info(context: LogContext, message: string): void {
    if (isDev) console.info(message, context)
  },

  warn(context: LogContext, message: string): void {
    if (isDev) {
      console.warn(message, context)
    } else {
      sentryCapture('warning', context, message)
    }
  },

  error(context: LogContext, message: string): void {
    if (isDev) {
      console.error(message, context)
    } else {
      sentryCapture('error', context, message)
    }
  }
}
